import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchHealth } from '../lib/api';

const metricLabels = {
  usuariosActivos: 'Usuarios activos',
  clientesActivos: 'Clientes activos',
  clientesTiendaNubeVinculados: 'Clientes Tiendanube',
  localidadesActivas: 'Localidades activas',
  tarifasMaestroActivas: 'Tarifas maestras activas',
  tarifasReglasActivas: 'Reglas activas',
};

const dateTimeFormatter = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const numberFormatter = new Intl.NumberFormat('es-AR');

function formatStatusLabel(status) {
  if (status === 'healthy') {
    return 'Operativo';
  }

  if (status === 'degraded') {
    return 'Con advertencias';
  }

  return 'Caido';
}

function formatDate(value) {
  if (!value) {
    return 'Sin datos';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Sin datos';
  }

  return dateTimeFormatter.format(date);
}

function formatMetric(value) {
  if (value === null || value === undefined) {
    return '-';
  }

  return numberFormatter.format(value);
}

export default function SystemStatus({ compact = false, title = 'Estado del sistema', subtitle }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadHealth = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await fetchHealth('/status');
      if (!result.data) {
        throw new Error('La API respondió sin contenido.');
      }

      setHealth(result.data);
      if (result.data.alerts?.length) {
        setError(result.data.alerts[0]);
      }
    } catch (err) {
      setHealth(null);
      setError(err.message || 'No se pudo consultar el estado del backend.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    const timer = window.setInterval(loadHealth, 60000);
    return () => window.clearInterval(timer);
  }, [loadHealth]);

  const metrics = useMemo(() => {
    const counters = health?.database?.counters || {};
    return Object.entries(metricLabels).map(([key, label]) => ({
      key,
      label,
      value: counters[key],
    }));
  }, [health]);

  const overallStatus = health?.status || (error ? 'down' : 'healthy');
  const apiStatus = health?.api?.status || (error ? 'down' : 'healthy');
  const dbStatus = health?.database?.status || (error ? 'down' : 'healthy');

  return (
    <section className={`status-panel ${compact ? 'status-panel--compact' : ''}`}>
      <div className="panel-head">
        <div>
          <p className="eyebrow">Integracion en linea</p>
          <h2>{title}</h2>
          <p className="panel-copy">{subtitle || 'API y base de datos reales, consultadas en cada actualizacion.'}</p>
        </div>
        <div className="panel-actions">
          <span className={`status-pill status-pill--${overallStatus}`}>{formatStatusLabel(overallStatus)}</span>
          <button type="button" className="btn-ghost" onClick={loadHealth} disabled={loading}>
            {loading ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="status-grid">
        <article className="status-card">
          <span className={`status-chip status-chip--${apiStatus}`}>API</span>
          <strong>{formatStatusLabel(apiStatus)}</strong>
          <span>{health?.api?.service || 'Cotizador.Api'}</span>
          <small>{health?.api?.environment || 'Entorno no disponible'}</small>
        </article>

        <article className="status-card">
          <span className={`status-chip status-chip--${dbStatus}`}>DB</span>
          <strong>{formatStatusLabel(dbStatus)}</strong>
          <span>Base de datos</span>
          <small>Monitoreo interno</small>
        </article>

        <article className="status-card">
          <span className="status-chip status-chip--info">Latencia</span>
          <strong>{health?.database?.latencyMs ?? '-'} ms</strong>
          <span>Chequeo real de conexion</span>
          <small>{formatDate(health?.checkedAtUtc)}</small>
        </article>

        <article className="status-card">
          <span className="status-chip status-chip--info">Version</span>
          <strong>{health?.api?.version || '-'}</strong>
          <span>{health?.status === 'degraded' ? 'Con advertencias' : 'Listo para cotizar'}</span>
          <small>Ultima verificacion</small>
        </article>
      </div>

      <div className="metric-grid">
        {metrics.map((metric) => (
          <article className="metric-card" key={metric.key}>
            <span>{metric.label}</span>
            <strong>{formatMetric(metric.value)}</strong>
          </article>
        ))}
      </div>
    </section>
  );
}
